import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled, { keyframes } from 'styled-components';
import compose from 'lodash/fp/compose';
import { useBlogCategories } from '../../hooks/useBlog';
import { getBlogPosts } from '../../actions';
import { withTranslation, withAccount } from '../hoc';
import { Clock, Eye, Person, Star, Search, ArrowRight, Filter } from 'react-bootstrap-icons';

// --- Brand Colors ---
const BRAND_COLOR = '#739bcb';
const BRAND_COLOR_DARK = '#2d5a90'; // Darker shade for gradients/active states
const TEXT_MAIN = '#739bcb'; // Very dark grey for titles
const TEXT_BODY = '#334155'; // Dark grey for clear reading
const BG_COLOR = '#f8fafc';

// --- Animations ---
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

// --- Styled Components ---

const BlogListWrapper = styled.div`
  min-height: 100vh;
  background-color: ${BG_COLOR};
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  color: ${TEXT_MAIN};
`;

const HeroSection = styled.section`
  position: relative;
  /* Using the specific brand color with a subtle gradient for depth */
  background: linear-gradient(135deg, ${BRAND_COLOR} 0%, ${BRAND_COLOR_DARK} 100%);
  padding: 6rem 2rem 8rem 2rem;
  text-align: center;
  color: #fff;
  overflow: hidden;

  h1 {
    font-size: 3.5rem;
    font-weight: 800;
    margin-bottom: 1.5rem;
    letter-spacing: -0.02em;
    animation: ${fadeIn} 0.6s ease-out;
  }

  p {
    font-size: 1.25rem;
    opacity: 0.95; /* Increased opacity for clarity */
    max-width: 600px;
    margin: 0 auto;
    line-height: 1.6;
    font-weight: 500;
    animation: ${fadeIn} 0.6s ease-out 0.1s backwards;
  }
`;

const ContentContainer = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 1.5rem 4rem 1.5rem;
  position: relative;
  z-index: 2;
`;

const FilterSection = styled.div`
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(12px);
  padding: 1.25rem;
  border-radius: 16px;
  box-shadow: 0 20px 40px -10px rgba(45, 90, 144, 0.15); /* Shadow tinted with brand color */
  margin-top: -4rem;
  margin-bottom: 3rem;
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  align-items: center;
  border: 1px solid rgba(255, 255, 255, 0.5);
  animation: ${fadeIn} 0.6s ease-out 0.2s backwards;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    margin-top: -2rem;
  }
`;

const SearchWrapper = styled.div`
  flex: 2;
  position: relative;
  min-width: 280px;

  svg {
    position: absolute;
    left: 16px;
    top: 50%;
    transform: translateY(-50%);
    color: #64748b;
    width: 18px;
    height: 18px;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 1rem 1rem 1rem 3rem;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 1rem;
  background: #fff;
  color: ${TEXT_MAIN};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${BRAND_COLOR};
    box-shadow: 0 0 0 4px rgba(45, 90, 144, 0.1);
  }

  &::placeholder {
    color: #94a3b8;
  }
`;

const CategorySelectWrapper = styled.div`
  position: relative;
  flex: 1;
  min-width: 200px;
  
  svg {
    position: absolute;
    left: 16px;
    top: 50%;
    transform: translateY(-50%);
    color: #64748b;
    z-index: 1;
  }
`;

const CategorySelect = styled.select`
  width: 100%;
  padding: 1rem 1rem 1rem 3rem;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 1rem;
  background: #fff;
  color: ${TEXT_MAIN};
  cursor: pointer;
  appearance: none;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${BRAND_COLOR};
    box-shadow: 0 0 0 4px rgba(45, 90, 144, 0.1);
  }
  
  background-image: url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%2364748b%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  background-size: 12px;
`;

const ResultsCount = styled.div`
  margin-bottom: 2rem;
  font-size: 0.95rem;
  color: #64748b;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 1px;

  strong {
    color: ${BRAND_COLOR};
    font-weight: 800;
  }
`;

const PostsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
  gap: 2.5rem;
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const BlogCard = styled(Link)`
  background: #fff;
  border-radius: 20px;
  overflow: hidden;
  border: 1px solid rgba(226, 232, 240, 0.8);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  text-decoration: none;
  height: 100%;

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 20px 25px -5px rgba(45, 90, 144, 0.15);
    border-color: ${BRAND_COLOR};
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  padding-top: 60%; 
  overflow: hidden;
  background: #e2e8f0;
`;

const CardImage = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: ${props => props.hasImage 
    ? `url(${props.image})` 
    : `linear-gradient(135deg, ${BRAND_COLOR} 0%, #cbd5e1 100%)`};
  background-size: cover;
  background-position: center;
  transition: transform 0.6s ease;

  ${BlogCard}:hover & {
    transform: scale(1.05);
  }
`;

const FeaturedBadge = styled.div`
  position: absolute;
  top: 1rem;
  left: 1rem;
  background: #fff;
  color: #d97706;
  padding: 0.4rem 0.8rem;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 700;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  text-transform: uppercase;
`;

const CardContent = styled.div`
  padding: 2rem;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const CategoryLabel = styled.span`
  color: ${BRAND_COLOR};
  font-size: 0.85rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.75rem;
  display: inline-block;
`;

const PostTitle = styled.h2`
  font-size: 1.4rem;
  color: ${TEXT_MAIN};
  margin: 0 0 1rem 0;
  font-weight: 800;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  transition: color 0.2s ease;

  ${BlogCard}:hover & {
    color: ${BRAND_COLOR};
  }
`;

const PostExcerpt = styled.p`
  color: ${TEXT_BODY}; /* Clearer, darker grey */
  font-size: 1.05rem;
  line-height: 1.6;
  margin: 0 0 1.5rem 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  flex: 1;
  font-weight: 400;
`;

const PostMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.85rem;
  color: #64748b;
  padding-top: 1.5rem;
  border-top: 1px solid #f1f5f9;
`;

const AuthorInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  
  .author-name {
    font-weight: 600;
    color: ${TEXT_MAIN};
  }
`;

const ReadMoreLink = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: ${BRAND_COLOR};
  font-weight: 700;
  font-size: 0.95rem;
  margin-top: 1rem;
  
  svg { 
    transition: transform 0.2s; 
    width: 16px;
    height: 16px;
  }
  
  ${BlogCard}:hover & svg { 
    transform: translateX(4px); 
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 6rem 2rem;
  background: #fff;
  border-radius: 20px;
  border: 1px dashed #cbd5e1;
  color: #64748b;

  svg {
    width: 50px;
    height: 50px;
    margin-bottom: 1rem;
    color: #cbd5e1;
  }

  h3 {
    font-size: 1.5rem;
    color: ${TEXT_MAIN};
    margin-bottom: 0.5rem;
  }
`;

const LoadingState = styled(EmptyState)`
  border: none;
  background: transparent;

  .spinner {
    width: 50px;
    height: 50px;
    border: 3px solid #e2e8f0;
    border-top-color: ${BRAND_COLOR};
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto 1.5rem;
  }

  @keyframes spin { to { transform: rotate(360deg); } }
`;

// --- Component ---

const BlogList = (props) => {
  const { tr, account } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const { categories } = useBlogCategories();
  
  const { posts, isLoading } = useSelector((state) => state.blogPost || { posts: [], isLoading: false });

  const isAuth = account?.user?.isAuth;
  const translate = isAuth ? tr : t;

  useEffect(() => {
    const params = {
      status: 'published',
    };
    
    if (searchTerm) {
      params.search = searchTerm; // Use 'search' for backend search
    }
    
    if (selectedCategory) {
      params.category__slug = selectedCategory; // Use 'category__slug' to match backend filterset_fields
    }

    dispatch(getBlogPosts(params));
  }, [dispatch, searchTerm, selectedCategory]);

  const postsData = posts?.results || posts || [];
  const categoriesData = categories?.results || categories || [];

  return (
    <BlogListWrapper>
      <HeroSection>
        <h1>{translate('AdminCat Blog')}</h1>
         <p>
          {translate('Expert insights on managing digital documents, finance, emails, and workspaces. Discover how to organize your life—from house, pets, and addresses to personal data—with our suite of mini SAP applications.')}
        </p>
      </HeroSection>

      <ContentContainer>
        <FilterSection>
          <SearchWrapper>
            {/* <Search /> */}
            <SearchInput
              type="text"
              placeholder={translate('Search articles...')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchWrapper>
          
          <CategorySelectWrapper>
            <Filter />
            <CategorySelect
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              <option value="">{translate('All Topics')}</option>
              {categoriesData.map(cat => (
                <option key={cat.id} value={cat.slug}>{cat.name}</option> // Use cat.slug for value
              ))}
            </CategorySelect>
          </CategorySelectWrapper>
        </FilterSection>

        {isLoading ? (
           <LoadingState>
             <div className="spinner" />
             <h3>{translate('Loading content...')}</h3>
           </LoadingState>
        ) : (
          <>
            {postsData.length > 0 && (
              <ResultsCount>
                Showing <strong>{postsData.length}</strong> {translate(postsData.length === 1 ? 'Article' : 'Articles')}
              </ResultsCount>
            )}

            {postsData.length === 0 ? (
              <EmptyState>
                <Search />
                <h3>{translate('No articles found')}</h3>
                <p>{translate('We couldn\'t find any posts matching your criteria.')}</p>
              </EmptyState>
            ) : (
              <PostsGrid>
                {postsData.map((post) => (
                  <BlogCard key={post.id} to={`/blog/${post.slug}`}>
                    <ImageWrapper>
                      {post.is_featured && (
                        <FeaturedBadge>
                          <Star size={12} /> {translate('Featured')}
                        </FeaturedBadge>
                      )}
                      <CardImage 
                        image={post.featured_image}
                        hasImage={!!post.featured_image}
                      />
                    </ImageWrapper>

                    <CardContent>
                      {post.category && (
                        <CategoryLabel>
                          {post.category.name}
                        </CategoryLabel>
                      )}

                      <PostTitle>{post.title}</PostTitle>

                      <PostExcerpt>
                        {post.excerpt || translate('Click to read more about this topic...')}
                      </PostExcerpt>

                      <ReadMoreLink>
                        {translate('Read Article')} <ArrowRight />
                      </ReadMoreLink>

                      <PostMeta>
                        <AuthorInfo>
                          <Person size={16} />
                          <span className="author-name">{post.author?.username || translate('Team')}</span>
                        </AuthorInfo>
                        
                        <div style={{ display: 'flex', gap: '12px' }}>
                          {/* <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Clock size={12} /> {Math.ceil((post.content?.split(' ').length || 0) / 200)}m
                          </span> */}
                          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Eye size={14} /> {post.views_count || 0}
                          </span>
                        </div>
                      </PostMeta>
                    </CardContent>
                  </BlogCard>
                ))}
              </PostsGrid>
            )}
          </>
        )}
      </ContentContainer>
    </BlogListWrapper>
  );
};

export default compose(withTranslation, withAccount)(BlogList);